import axios from 'axios';
import { API_V1_URL } from '../config';

// Singleton in-flight refresh promise. Without this, N concurrent 401s
// (e.g. a page that fires many parallel requests on mount) each kick off
// their own `/auth/refresh-token` call, the server rotates tokens N times,
// and only the last response's token is the one that "wins" — earlier
// responses' tokens are silently discarded and the retries with stale tokens
// fail. Sharing one promise across all 401s collapses the burst to one call.
let inFlightRefresh = null;

function refreshOnce() {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = axios
    .post(`${API_V1_URL}/auth/refresh-token`, {}, { withCredentials: true })
    .then((res) => {
      if (!res.data?.success) throw new Error('Refresh response not successful');
      const newToken = res.data.data?.accessToken;
      if (!newToken) throw new Error('Refresh response missing accessToken');
      localStorage.setItem('accessToken', newToken);
      // Tell socketService (if loaded) to swap to the new token. Dynamic
      // require so this module can be used in non-socket contexts without
      // pulling socket.io-client into the bundle eagerly.
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const socketService = require('./socketService').default;
        if (socketService && typeof socketService.updateToken === 'function') {
          socketService.updateToken(newToken);
        }
      } catch (_) { /* socket not loaded — that's fine */ }
      return newToken;
    })
    .finally(() => {
      inFlightRefresh = null;
    });
  return inFlightRefresh;
}

function isAuthFailure(error) {
  if (error.response?.status !== 401) return false;
  return true;
}

/**
 * Applies shared request/response interceptors to an axios instance.
 * - Request: attaches Bearer token from localStorage
 * - Response: auto-refreshes expired JWT tokens (shared refresh promise),
 *             redirects to /login for terminal auth failures.
 */
export function applyInterceptors(instance) {
  // Request interceptor - add auth token
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor - handle token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};

      // Only attempt refresh on the documented "TOKEN_EXPIRED" signal —
      // generic 401s (Invalid token / User not found / inactive) are
      // terminal, kick the user to /login below.
      const isExpired =
        error.response?.status === 401 &&
        error.response?.data?.code === 'TOKEN_EXPIRED' &&
        !originalRequest._retry;

      if (isExpired) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshOnce();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('accessToken');
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      // Other 401s (Invalid token, User not found, inactive) — also kick to
      // /login instead of silently propagating. The previous behaviour was
      // to swallow these errors, which left the UI stuck on broken pages.
      if (isAuthFailure(error) && !originalRequest._authHandled) {
        originalRequest._authHandled = true;
        localStorage.removeItem('accessToken');
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Rate limited — show a clear message
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        let waitMsg = '';
        if (retryAfter) {
          const secs = parseInt(retryAfter, 10);
          if (!Number.isNaN(secs)) {
            waitMsg = secs >= 60
              ? `Try again in ${Math.ceil(secs / 60)} min.`
              : `Try again in ${secs}s.`;
          }
        }
        error.message = waitMsg
          ? `Too many requests. ${waitMsg}`
          : 'Too many requests. Please wait a moment and try again.';
      }

      // 402 Payment Required — Business API / MM Lite / Campaign sends now
      // reserve balance atomically and reject with 402 when the tenant is
      // out of credit. Tag the error so pages can surface a recharge CTA
      // (or just show the friendly message). We don't auto-redirect because
      // some flows want to display the error in-line (e.g. send button).
      if (error.response?.status === 402) {
        error.isInsufficientBalance = true;
        error.message =
          error.response?.data?.message ||
          'Insufficient balance. Please top up to continue sending.';
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
